import React, { useEffect, useState } from 'react';
import {
  getAllExpenseFormReport,
  getAllExpenseReports,
} from '@services/expense-reports.service';
import { QueryRequestHelper } from '@common/query-request-helper';
import { getValue } from '@utils/lodash';
import Loader from '@components/common/Loader';
import { convertCurrentDateWithTime } from "@common/date-helpers";
import { formatAmount, formatText, concatString } from "@common/text-helpers";
import { getColorStyle, getSingleColor } from "@common/color";
import { Tooltip } from "react-tooltip";
import ReportsSvgComponent from "@assets/svg-components/sidebar/reports";
import DocumentSvgComponent from "@assets/svg-components/document";
import ViolationSvgComponent from "@assets/svg-components/violation";
import { useNavigate } from 'react-router-dom';
import './styles.scss';

const ReportsList = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(10);
  const [showDurationColumns, setShowDurationColumns] = useState({
    durationFrom: false,
    durationTo: false,
  });

  useEffect(() => {
    getData();
    getFormData();
  }, []);

  const getData = async () => {
    let payload: any = {
      page_no: 1,
      page_size: limit,
      filter_type: "list",
    };
    let queryRequest = QueryRequestHelper(payload);
    try {
      setIsLoading(true);
      let resp = await getAllExpenseReports(queryRequest);
      if (resp) {
        setList(getValue(resp, `data.expense_reports`, []));
        setTotalCount(getValue(resp, `data.pagination.total`, 0));
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const getFormData = async () => {
    try {
      const queryRequest = QueryRequestHelper({});
      const resp = await getAllExpenseFormReport(queryRequest);

      if (resp) {
        const formLayout = getValue(resp, `data.form_layout`, []);
        setShowDurationColumns({
          durationFrom: formLayout.some(
            (item: any) =>
              getValue(item, `field_name`, '') === 'duration_from' &&
              getValue(item, `is_enabled`, false),
          ),
          durationTo: formLayout.some(
            (item: any) =>
              getValue(item, `field_name`, '') === 'duration_to' &&
              getValue(item, `is_enabled`, false),
          ),
        });
      }
    } catch (error) {}
  };

  const handleNavigate = (id: string) => {
    const queryRequest = QueryRequestHelper({});
    navigate(`/client/reports/detail/${id}?${queryRequest}`);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <> 
    <div className="table-responsive-container">
      <div className="table-wrapper">
        <table className="datatable_table">
          <thead className="datatable_table_thead">
            <tr>
              <th className="datatable_table_th" style={{ width: "50px" }}></th>
              <th className="datatable_table_th" style={{ width: "100px" }}>
                <h6 className="">Report ID</h6>
              </th>
              <th className="datatable_table_th" style={{ width: "220px" }}>
                <h6 className="">Report Name</h6>
              </th>
              <th className="datatable_table_th" style={{ width: "120px" }}>
                <h6 className="">Total</h6>
              </th>
              {showDurationColumns.durationFrom && (
                <th className="datatable_table_th" style={{ width: "160px" }}>
                  <h6 className="">Duration From</h6>
                </th>
              )}
              {showDurationColumns.durationTo && (
                <th className="datatable_table_th" style={{ width: "160px" }}>
                  <h6 className="">Duration To</h6>
                </th>
              )}
              <th className="datatable_table_th" style={{ width: "150px" }}>
                <h6 className="">Status</h6>
              </th>
              <th className="datatable_table_th" style={{ width: "120px" }}></th>
            </tr>
          </thead>
          <tbody className="datatable_table_tbody">
            {list.length > 0 ? (
              list.map((item: any, index: number) => (
                <tr key={index} className="datatable_table_tr">
                  <td className="datatable_table_td" style={{ width: "70px" }}>
                    <ReportsSvgComponent
                      color={getSingleColor(getValue(item, `approval_status`, ""))}
                      size={24}
                    />
                  </td>
                  <td
                    className="datatable_table_td align-items-center"
                    onClick={() => handleNavigate(getValue(item, `id`, ""))}
                  >
                    <div className="d-flex">
                      <div>
                        <h6 className="header_text color_primary">
                          {getValue(item, `report_id`, "")}
                        </h6>
                      </div>
                    </div>
                  </td>
                  <td
                    className="datatable_table_td"
                    onClick={() => handleNavigate(getValue(item, `id`, ""))}
                  >
                    <h6 className="">
                      {concatString(getValue(item, `report_name`, ""), 30)}
                    </h6>
                  </td>
                  <td
                    onClick={() => handleNavigate(getValue(item, `id`, ""))}
                    className="datatable_table_td"
                  >
                    <h6 className="">
                      {getValue(item, `currency_symbol`, "")} {formatAmount(getValue(item, `total`, ""))}
                    </h6>
                  </td>
                  {showDurationColumns.durationFrom && (
                    <td
                      className="datatable_table_td"
                      onClick={() => handleNavigate(getValue(item, `id`, ""))}
                    >
                      <h6 className="">
                        {convertCurrentDateWithTime(getValue(item, `duration_from`, ""))}
                      </h6>
                    </td>
                  )}
                  {showDurationColumns.durationTo && (
                    <td
                      className="datatable_table_td"
                      onClick={() => handleNavigate(getValue(item, `id`, ""))}
                    >
                      <h6 className="">
                        {convertCurrentDateWithTime(getValue(item, `duration_to`, ""))}
                      </h6>
                    </td>
                  )}
                  <td
                    className="datatable_table_td"
                    onClick={() => handleNavigate(getValue(item, `id`, ""))}
                  >
                    <h6
                      className=""
                      style={getColorStyle(getValue(item, `approval_status`, ""))}
                    >
                      {formatText(getValue(item, `approval_status`, ""))}
                    </h6>
                  </td>
                  <td className="datatable_table_td">
                    <div className="d-flex align-items-center">
                      <div
                        data-tooltip-id={getValue(item, `receipts.length`, 0) > 0 ? "my-tooltip-document" : ""}
                        data-tooltip-content="This expense contains receipts"
                      >
                        <DocumentSvgComponent
                          color={getValue(item, `receipts.length`, 0) > 0 ? "#276EF1" : "lightgray"}
                        />
                      </div>
                      <div
                        data-tooltip-id={!getValue(item, `expense_entries[${0}].is_policy_violated`, false) ? "" : "my-tooltip"}
                        data-tooltip-content="This expense violated certain policies"
                        className="ms-4"
                      >
                        <ViolationSvgComponent
                          color={getValue(item, `expense_entries[${0}].is_policy_violated`, false) ? "#ff8a00" : "lightgray"}
                          size={20}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center">
                  No reports found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Tooltip id="my-tooltip" place="bottom" />
      <Tooltip id="my-tooltip-document" place="bottom" />
    </div>

    <div className="d-flex justify-content-end">
      <button className='btn btn-primary p-2 px-4 mt-3' onClick={() => navigate('/client/reports')}>View All</button>
    </div>
    </>
  );
};

export default ReportsList; 
